import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Info, AlertTriangle, Shield, Layers, Clock, ExternalLink } from 'lucide-react';
import { apiService } from '../../services/api';
import CustomSelect from '../common/CustomSelect';
import Tooltip from '../common/Tooltip';
import QueueCreationModal from './QueueCreationModal';
import { useContextualNavigation } from '../../hooks/useContextualNavigation';
import type { Queue, WorkflowStep, QueueConsumerConfig, ConsumerRole } from '../../types';

/**
 * Configuration for queue_read step
 */
interface QueueReadConfig {
  source_queue_id?: number;
  source_queue_name?: string;
  consumption_mode: 'single' | 'batch';
  batch_size?: number;
  visibility_timeout_seconds: number;
  consumer_role?: ConsumerRole;
}

interface QueueReadStepConfigProps {
  step: WorkflowStep;
  projectId: string;
  onConfigChange: (config: QueueReadConfig) => void;
  disabled?: boolean;
}

interface FormErrors {
  source_queue_id?: string;
  batch_size?: string;
  visibility_timeout?: string;
}

/**
 * Formats seconds into a human-readable string
 */
const formatTimeout = (seconds: number): string => {
  if (seconds >= 3600) {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return mins > 0 ? `${hours} hour${hours > 1 ? 's' : ''} ${mins} minute${mins > 1 ? 's' : ''}` : `${hours} hour${hours > 1 ? 's' : ''}`;
  } else if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${minutes} minute${minutes > 1 ? 's' : ''} ${secs} seconds` : `${minutes} minute${minutes > 1 ? 's' : ''}`;
  }
  return `${seconds} seconds`;
};

/**
 * QueueReadStepConfig - Configuration panel for queue_read step type
 *
 * This component allows users to configure how data is read from a queue
 * to initiate a workflow. Queue read is an INITIATOR step that starts
 * workflow by reading from a queue.
 *
 * Configuration options:
 * - Source queue selection (required)
 * - Consumption mode: single or batch
 * - Batch size (only visible in batch mode, 1-500)
 * - Visibility timeout (60-3600 seconds)
 */
const QueueReadStepConfig: React.FC<QueueReadStepConfigProps> = ({
  step,
  projectId,
  onConfigChange,
  disabled = false,
}) => {
  // AC-1393: open the selected queue's detail page from this modal so the
  // user can inspect/edit the queue without leaving the workflow editor.
  const { navigateWithContext } = useContextualNavigation();

  // Queue list state
  const [queues, setQueues] = useState<Queue[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Consumer config state for role selection (checks step configs, not active registrations)
  const [consumerConfigs, setConsumerConfigs] = useState<QueueConsumerConfig[]>([]);
  const [consumersLoading, setConsumersLoading] = useState(false);

  // Form state - initialize from step.config or defaults
  const existingConfig = step.config as QueueReadConfig | undefined;
  const [sourceQueueId, setSourceQueueId] = useState<number | undefined>(
    existingConfig?.source_queue_id
  );
  const [consumptionMode, setConsumptionMode] = useState<'single' | 'batch'>(
    existingConfig?.consumption_mode || 'single'
  );
  const [batchSize, setBatchSize] = useState<number>(
    existingConfig?.batch_size || 50
  );
  const [visibilityTimeout, setVisibilityTimeout] = useState<number>(
    existingConfig?.visibility_timeout_seconds || 300
  );
  const [consumerRole, setConsumerRole] = useState<ConsumerRole>(
    existingConfig?.consumer_role || 'PRIMARY'
  );

  // UI state
  const [errors, setErrors] = useState<FormErrors>({});

  // Debounce ref for config changes
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const lastConfigRef = useRef<string>('');

  // Fetch queues - extracted so it can be re-run after creating a new queue inline
  const fetchQueues = useCallback(async () => {
    if (!projectId) {
      setIsLoading(false);
      setLoadError('Project ID is required');
      return;
    }
    setIsLoading(true);
    setLoadError(null);
    try {
      const queueList = await apiService.getQueues(projectId);
      setQueues(queueList);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch queues';
      if (message.toLowerCase().includes('timeout')) {
        setLoadError('Request took too long. Please try again.');
      } else {
        setLoadError(`Error: ${message}`);
      }
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchQueues();
  }, [fetchQueues]);

  // Handle queue created from inline modal: refresh list and auto-select it
  const handleQueueCreated = async (newQueue: Queue) => {
    setIsModalOpen(false);
    await fetchQueues();
    setSourceQueueId(newQueue.queue_id);
  };

  // Fetch consumer configs when queue is selected (checks step configs, not active registrations)
  useEffect(() => {
    if (!sourceQueueId) {
      setConsumerConfigs([]);
      return;
    }

    const fetchConsumerConfigs = async () => {
      setConsumersLoading(true);
      try {
        // Get workflows configured to consume from this queue, excluding current orchestration
        const workflowId = step.orchestration_id ? Number(step.orchestration_id) : undefined;
        const configs = await apiService.getQueueConsumerConfigs(sourceQueueId, workflowId);
        setConsumerConfigs(configs);
      } catch (err) {
        console.error('Failed to fetch consumer configs:', err);
        setConsumerConfigs([]);
      } finally {
        setConsumersLoading(false);
      }
    };

    fetchConsumerConfigs();
  }, [sourceQueueId, step.orchestration_id]);

  // Get selected queue info
  const selectedQueue = queues.find(q => q.queue_id === sourceQueueId);
  const isCircuitOpen = selectedQueue?.circuit_breaker_state === 'OPEN';
  const isSingleConsumerMode = selectedQueue?.single_consumer_mode ?? false;

  // Determine if another workflow is already configured as PRIMARY for this queue
  const hasPrimaryConsumer = consumerConfigs.some(
    c => c.consumer_role === 'PRIMARY'
  );

  // Get the workflow name that has PRIMARY configured (for informational display)
  const primaryWorkflow = consumerConfigs.find(c => c.consumer_role === 'PRIMARY');

  // Determine available roles based on queue state
  const getAvailableRoles = (): ConsumerRole[] => {
    if (!sourceQueueId) return ['PRIMARY', 'PARALLEL', 'BACKUP'];

    if (isSingleConsumerMode) {
      // Single consumer mode: only BACKUP if PRIMARY exists
      if (hasPrimaryConsumer) {
        return ['BACKUP'];
      }
      return ['PRIMARY', 'BACKUP'];
    }

    // Multi-consumer mode
    if (hasPrimaryConsumer) {
      return ['PARALLEL', 'BACKUP'];
    }
    return ['PRIMARY', 'PARALLEL', 'BACKUP'];
  };

  const availableRoles = getAvailableRoles();

  // Auto-select first available role if current selection is not available
  useEffect(() => {
    if (availableRoles.length > 0 && !availableRoles.includes(consumerRole)) {
      setConsumerRole(availableRoles[0]);
    }
  }, [availableRoles, consumerRole]);

  // Debounced config change handler
  const notifyConfigChange = useCallback((config: QueueReadConfig) => {
    const configStr = JSON.stringify(config);
    if (configStr === lastConfigRef.current) {
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      lastConfigRef.current = configStr;
      onConfigChange(config);
    }, 300);
  }, [onConfigChange]);

  // Build and notify config on form changes
  useEffect(() => {
    const config: QueueReadConfig = {
      source_queue_id: sourceQueueId,
      source_queue_name: sourceQueueId ? queues.find(q => q.queue_id === sourceQueueId)?.name : undefined,
      consumption_mode: consumptionMode,
      visibility_timeout_seconds: visibilityTimeout,
      consumer_role: consumerRole,
    };

    if (consumptionMode === 'batch') {
      config.batch_size = batchSize;
    }

    notifyConfigChange(config);
  }, [sourceQueueId, queues, consumptionMode, batchSize, visibilityTimeout, consumerRole, notifyConfigChange]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Handle queue selection
  const handleQueueChange = (value: string) => {
    const queueId = value ? parseInt(value, 10) : undefined;
    setSourceQueueId(queueId);
    if (queueId) {
      setErrors(prev => ({ ...prev, source_queue_id: undefined }));
    }
  };

  // Handle consumption mode change
  const handleConsumptionModeChange = (value: 'single' | 'batch') => {
    setConsumptionMode(value);
  };

  // Handle batch size change with validation
  const handleBatchSizeChange = (value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue)) {
      setBatchSize(numValue);
      // Inline validation
      if (numValue < 1) {
        setErrors(prev => ({ ...prev, batch_size: 'Batch size must be at least 1' }));
      } else if (numValue > 500) {
        setErrors(prev => ({ ...prev, batch_size: 'Batch size cannot exceed 500' }));
      } else {
        setErrors(prev => ({ ...prev, batch_size: undefined }));
      }
    }
  };

  // Handle visibility timeout change with validation
  const handleVisibilityTimeoutChange = (value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue)) {
      setVisibilityTimeout(numValue);
      // Inline validation
      if (numValue < 60) {
        setErrors(prev => ({ ...prev, visibility_timeout: 'Visibility timeout must be at least 60 seconds' }));
      } else if (numValue > 3600) {
        setErrors(prev => ({ ...prev, visibility_timeout: 'Visibility timeout cannot exceed 3600 seconds (1 hour)' }));
      } else {
        setErrors(prev => ({ ...prev, visibility_timeout: undefined }));
      }
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="p-4">
        <p className="text-gray-500 dark:text-gray-400">Loading queues...</p>
      </div>
    );
  }

  // Render error state
  if (loadError) {
    return (
      <div className="p-4">
        <p className="text-red-600 dark:text-red-400">{loadError}</p>
      </div>
    );
  }

  // Render empty state
  if (queues.length === 0) {
    return (
      <div className="p-4">
        <p className="text-yellow-600 dark:text-yellow-400">No queues available. Please create a queue first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Help text */}
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Reads from a queue and starts the workflow. This is an initiator step that processes items from the selected queue.
      </p>

      {/* Source Queue Selection */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <label htmlFor="source-queue" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Source Queue
            </label>
            {sourceQueueId && (
              <Tooltip content="Open queue page">
                <button
                  type="button"
                  onClick={() =>
                    navigateWithContext(
                      `/projects/${projectId}/queues/${sourceQueueId}`,
                      'Workflow',
                    )
                  }
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300"
                  aria-label="Open queue page"
                  data-testid="open-source-queue-link"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </Tooltip>
            )}
          </div>
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            disabled={disabled}
            data-testid="create-new-queue-link"
            className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Create new queue
          </button>
        </div>
        <CustomSelect
          value={sourceQueueId?.toString() || ''}
          onChange={(value) => handleQueueChange(value)}
          options={queues.map((queue) => ({
            value: queue.queue_id.toString(),
            label: queue.name,
          }))}
          placeholder="Select a queue..."
          disabled={disabled}
          aria-label="Source Queue"
        />
        {errors.source_queue_id && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.source_queue_id}</p>
        )}
      </div>

      {/* Circuit Breaker Warning */}
      {isCircuitOpen && (
        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
          <p className="text-sm text-yellow-800 dark:text-yellow-300">
            Warning: Circuit breaker is OPEN for this queue. Reads will be blocked until the circuit is reset.
          </p>
        </div>
      )}

      {/* Consumer Role Selection - only show when queue is selected */}
      {sourceQueueId && (
        <div>
          <label htmlFor="consumer-role" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Consumer Role
          </label>
          {consumersLoading ? (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Loading consumer information...</p>
          ) : (
            <>
              {/* Show info about existing consumers */}
              {hasPrimaryConsumer && primaryWorkflow && (
                <div className="mt-1 mb-2 p-2 bg-primary-50 dark:bg-blue-900/20 border border-primary-200 dark:border-blue-800 rounded-md">
                  <p className="text-sm text-blue-800 dark:text-blue-300 flex items-center">
                    <Shield className="w-4 h-4 mr-1" />
                    <span>
                      <strong>{primaryWorkflow.orchestration_name}</strong> is already configured as PRIMARY for this queue.
                      {isSingleConsumerMode
                        ? ' Single consumer mode is enabled - only BACKUP role is available.'
                        : ' Select PARALLEL to process concurrently, or BACKUP for failover.'}
                    </span>
                  </p>
                </div>
              )}
              {isSingleConsumerMode && !hasPrimaryConsumer && (
                <div className="mt-1 mb-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
                  <p className="text-sm text-amber-800 dark:text-amber-300 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    Single consumer mode is enabled. Only one PRIMARY consumer is allowed.
                  </p>
                </div>
              )}
              <CustomSelect
                value={consumerRole}
                onChange={(value) => setConsumerRole(value as ConsumerRole)}
                options={availableRoles.map((role) => ({
                  value: role,
                  label: role,
                }))}
                disabled={disabled || availableRoles.length === 1}
                aria-label="Consumer Role"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {consumerRole === 'PRIMARY' && (
                  <span className="flex items-center">
                    <Shield className="w-3 h-3 mr-1" />
                    Main consumer - triggered by queue scheduling.
                  </span>
                )}
                {consumerRole === 'PARALLEL' && (
                  <span className="flex items-center">
                    <Layers className="w-3 h-3 mr-1" />
                    Processes items concurrently with the primary consumer.
                  </span>
                )}
                {consumerRole === 'BACKUP' && (
                  <span className="flex items-center">
                    <Clock className="w-3 h-3 mr-1" />
                    Failover consumer - only processes when primary is unavailable.
                  </span>
                )}
              </p>
            </>
          )}
        </div>
      )}

      {/* Consumption Mode Selection */}
      <fieldset>
        <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          <span id="consumption-mode-label">Consumption Mode</span>
        </legend>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Single mode processes one item at a time. Batch mode processes multiple items in each run.
        </p>
        <div className="space-y-2" role="radiogroup" aria-labelledby="consumption-mode-label">
          <label className="flex items-center">
            <input
              type="radio"
              name="consumption-mode"
              id="consumption-mode-single"
              aria-label="Single"
              value="single"
              checked={consumptionMode === 'single'}
              onChange={() => handleConsumptionModeChange('single')}
              disabled={disabled}
              className="mr-2"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Single</span>
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">- Process one item at a time</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="consumption-mode"
              id="consumption-mode-batch"
              aria-label="Batch"
              value="batch"
              checked={consumptionMode === 'batch'}
              onChange={() => handleConsumptionModeChange('batch')}
              disabled={disabled}
              className="mr-2"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Batch</span>
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">- Process multiple items per run</span>
          </label>
        </div>
      </fieldset>

      {/* Batch Size Input (conditional) */}
      {consumptionMode === 'batch' && (
        <div>
          <label htmlFor="batch-size" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Batch Size
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            Number of items to process per batch. Recommended default is 50. Maximum is 500.
          </p>
          <input
            type="number"
            id="batch-size"
            min="1"
            max="500"
            value={batchSize}
            onChange={(e) => handleBatchSizeChange(e.target.value)}
            disabled={disabled}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-700"
          />
          {errors.batch_size && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.batch_size}</p>
          )}
        </div>
      )}

      {/* Visibility Timeout Input */}
      <div>
        <label htmlFor="visibility-timeout" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Visibility Timeout (seconds)
          <Tooltip content="How long an item stays hidden after being read. This visibility timeout prevents other consumers from processing the same item while it's being worked on.">
            <Info
              className="inline-block ml-1 w-4 h-4 cursor-help text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              aria-label="help"
            />
          </Tooltip>
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
          Range: 60-3600 seconds. Current: {formatTimeout(visibilityTimeout)}
        </p>
        <input
          type="number"
          id="visibility-timeout"
          min="60"
          max="3600"
          value={visibilityTimeout}
          onChange={(e) => handleVisibilityTimeoutChange(e.target.value)}
          disabled={disabled}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-700"
        />
        {errors.visibility_timeout && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.visibility_timeout}</p>
        )}
      </div>

      {/* Inline queue creation modal */}
      <QueueCreationModal
        projectId={projectId}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onQueueCreated={handleQueueCreated}
      />
    </div>
  );
};

export default QueueReadStepConfig;
